/**
 * Diálogo "insertar vídeo" (F3-F): subir uno NUEVO o elegir de la BIBLIOTECA
 * del curso (solo `ready` es seleccionable). Devuelve el `videoAssetId` elegido.
 * Lo usan el editor de bloques (`video`) y la autoría del módulo `video`.
 */
import { type Component, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Library, Upload } from "lucide-solid";
import type { VideoAsset } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { MediaApi } from "../lib/media-api";
import { VideoUpload } from "./video-upload";
import { VideoLibrary } from "./video-library";

export interface VideoPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
  lessonId?: string | null;
  api?: MediaApi;
  /** Devuelve el id del `video_asset` elegido (o recién subido). */
  onPick: (videoId: string) => void;
}

type Tab = "library" | "upload";

const tabBtn = (active: boolean) =>
  `flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
    active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
  }`;

export const VideoPickerDialog: Component<VideoPickerDialogProps> = (props) => {
  const { t } = useI18n();
  const [tab, setTab] = createSignal<Tab>("library");

  const pick = (videoId: string) => {
    props.onPick(videoId);
    props.onOpenChange(false);
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-2xl">
      <DialogHeader size="md">
        <DialogTitle class="text-lg font-bold">{t(msg`Insert video`)}</DialogTitle>
      </DialogHeader>

      <div class="space-y-4 p-4">
        <div class="flex gap-2" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab() === "library"}
            class={tabBtn(tab() === "library")}
            data-testid="video-picker-tab-library"
            onClick={() => setTab("library")}
          >
            <Library class="h-4 w-4" /> {t(msg`Library`)}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab() === "upload"}
            class={tabBtn(tab() === "upload")}
            data-testid="video-picker-tab-upload"
            onClick={() => setTab("upload")}
          >
            <Upload class="h-4 w-4" /> {t(msg`Upload new`)}
          </button>
        </div>

        <Show
          when={tab() === "library"}
          fallback={
            <VideoUpload
              courseId={props.courseId}
              lessonId={props.lessonId}
              api={props.api}
              onUploaded={(asset) => pick(asset.id)}
            />
          }
        >
          <VideoLibrary
            courseId={props.courseId}
            api={props.api}
            selectable
            onSelect={(video: VideoAsset) => pick(video.id)}
          />
        </Show>
      </div>
    </Dialog>
  );
};
